// ============================================================================
// GST EXTENSION SERVICE — statutory due-date extensions (ADR-0021 §6, §7)
// ============================================================================
// Not paginated, deliberately. Extensions are notifications, and a firm sees a
// handful a year, not a page of them.

import { get, post } from '../client/axiosClient';
import { gstEndpoints } from '../endpoints/gst.endpoints';
import { buildEndpoint } from '../endpoints/base.endpoints';
import type {
  ObligationExtension,
  CreateObligationExtensionRequest,
  ObligationExtensionApplyResult,
  ObligationState,
  ObligationStateCohort,
  ObligationStateScope,
  ObligationStatutoryDates,
} from '../types/obligationExtension.types';

class GstExtensionService {
  async getAll(): Promise<ObligationExtension[]> {
    const response = await get<any>(gstEndpoints.extensions());
    return (response.data?.data ?? []) as ObligationExtension[];
  }

  /**
   * The states an extension can be scoped to, and the deadline cohorts among
   * them.
   *
   * Cohorts are derived server-side from the due-date rules — each is a state
   * list and the day it produces — so the picker never shows an internal group
   * code, and the fall-through arrives already expanded to the states nobody
   * named.
   */
  async getStateScope(): Promise<ObligationStateScope> {
    const response = await get<any>(gstEndpoints.extensionStates());
    const data = response.data?.data;
    return {
      states: (data?.states ?? []) as ObligationState[],
      cohorts: (data?.cohorts ?? []) as ObligationStateCohort[],
    };
  }

  /**
   * The statutory deadline(s) this scope would replace — what the extended date
   * has to beat.
   *
   * A LIST, because one scope can span obligations with different deadlines
   * (monthly 20th, QRMP group X 22nd, group Y 24th). The form shows them so the
   * date is typed against something rather than from memory.
   *
   * The query is built by hand rather than through `buildEndpointWithQuery`,
   * which stringifies an array to "32,33" — ASP.NET Core binds `string[]` from
   * REPEATED keys, so a joined value would arrive as one nonsense state code.
   */
  async getStatutoryDates(params: {
    formCode: string;
    periodStart: string;
    schemeCode?: string | null;
    stateCodes?: string[] | null;
  }): Promise<ObligationStatutoryDates> {
    const query = new URLSearchParams();
    query.set('formCode', params.formCode);
    query.set('periodStart', params.periodStart);
    if (params.schemeCode) query.set('schemeCode', params.schemeCode);
    for (const code of params.stateCodes ?? []) query.append('stateCodes', code);

    const endpoint = `${buildEndpoint(gstEndpoints.extensionStatutoryDatesPath())}?${query.toString()}`;
    const response = await get<any>(endpoint);
    const data = response.data?.data;
    return {
      dates: (data?.dates ?? []) as string[],
      isQuarterStart: Boolean(data?.isQuarterStart),
    };
  }

  /**
   * Records a notification. Changes no deadline — applying is separate, so the
   * scope can be previewed before hundreds of dates move.
   */
  async create(request: CreateObligationExtensionRequest): Promise<ObligationExtension> {
    const response = await post<any>(gstEndpoints.extensions(), request);
    return response.data?.data as ObligationExtension;
  }

  /** Reports what applying WOULD change, including what it would skip and why. */
  async preview(id: string): Promise<ObligationExtensionApplyResult> {
    const response = await get<any>(gstEndpoints.extensionPreview(id));
    return response.data?.data as ObligationExtensionApplyResult;
  }

  /** Stamps the extension onto every obligation it covers. Idempotent. */
  async apply(id: string): Promise<ObligationExtensionApplyResult> {
    const response = await post<any>(gstEndpoints.extensionApply(id), {});
    return response.data?.data as ObligationExtensionApplyResult;
  }

  /** Clears it from every obligation carrying it, restoring the statutory date. */
  async revoke(id: string): Promise<number> {
    const response = await post<any>(gstEndpoints.extensionRevoke(id), {});
    return (response.data?.data ?? 0) as number;
  }
}

export const gstExtensionService = new GstExtensionService();
export default gstExtensionService;
